// src/components/info/InfoAccordion.jsx
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Accordion } from "../ui/Accordion";
import { resolveInfoContent } from "../../utils/resolveInfoContent";

/**
 * Props:
 *  infoKeys: string[] // e.g. ["info.lungAge", "info.lungFitnessScore", ...]
 *  allowMultiple?: boolean
 *  titleFallbacks?: Record<string, string>
 *  deepLinking?: boolean // open by URL hash (e.g. #lungAge)
 *  scrollIntoViewOnOpen?: boolean // useful in bottom sheet
 */
export default function InfoAccordion({
  infoKeys,
  allowMultiple = true,
  titleFallbacks = {},
  deepLinking = true,
  scrollIntoViewOnOpen = false,
}) {
  const { t } = useTranslation();

  const items = useMemo(() => {
    return infoKeys
      .map((key) => {
        const lastToken = key.split(".").slice(-1)[0]; // e.g. "lungAge"
        const content = resolveInfoContent({
          t,
          infoKey: key,
          titleFallback: titleFallbacks[key],
        });
        if (!content) return null;

        // Pull image data straight from i18n (optional)
        const imagePath = t(`${key}.imagePath`, { defaultValue: "" });
        const imageAlt = t(`${key}.imageAlt`, {
          defaultValue: content.title || lastToken,
        });

        const body = (
          <div className="space-y-3">
            {content.intro ? (
              <p className="whitespace-pre-line">{content.intro}</p>
            ) : null}
            {content.sections?.map((s, idx) => (
              <section key={idx} className="space-y-1">
                {s.heading && (
                  <h4 className="text-typography font-semibold">{s.heading}</h4>
                )}
                {s.body && <p className="whitespace-pre-line">{s.body}</p>}
              </section>
            ))}
            {content.singleBody ? (
              <p className="whitespace-pre-line">{content.singleBody}</p>
            ) : null}
            {imagePath ? (
              <figure className="pt-2">
                <img
                  src={imagePath}
                  alt={imageAlt}
                  loading="lazy"
                  decoding="async"
                  referrerPolicy="no-referrer"
                  className="w-full rounded-xl border border-border/50 object-cover"
                />
                {content.subtitle ? (
                  <figcaption className="mt-2 text-xs text-typography/70">
                    {content.subtitle}
                  </figcaption>
                ) : null}
              </figure>
            ) : null}
          </div>
        );

        return {
          id: lastToken, // for deep-linking (#lungAge)
          title: content.title || lastToken,
          content: body,
        };
      })
      .filter(Boolean);
  }, [infoKeys, t, titleFallbacks]);

  // Open a section from URL hash (e.g. /info#lungAge)
  const defaultOpen = useMemo(() => {
    if (!deepLinking) return [];
    const hash = typeof window !== "undefined" ? window.location.hash : "";
    const id = hash.startsWith("#") ? hash.slice(1) : "";
    return id ? [id] : [];
  }, [deepLinking]);

  // Keep hash in sync when user opens an item (first item only, for simplicity)
  useEffect(() => {
    if (!deepLinking) return;
    const onClick = (e) => {
      const btn = e.target.closest("button[id$='-button']");
      if (!btn) return;
      const idAttr = btn.getAttribute("id") || "";
      const id = idAttr.split("-").slice(-2, -1)[0]; // the item id we injected
      if (
        id &&
        typeof window !== "undefined" &&
        window?.history?.replaceState
      ) {
        const url = `${window.location.pathname}${window.location.search}#${id}`;
        window.history.replaceState(null, "", url);
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [deepLinking]);

  return (
    <Accordion
      items={items}
      allowMultiple={allowMultiple}
      defaultOpenIds={defaultOpen}
      scrollIntoViewOnOpen={scrollIntoViewOnOpen}
    />
  );
}
